import React, { useEffect, useMemo } from 'react';
import {
  RouterProvider,
  type RouteObject,
  type createBrowserRouter,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  ROUTES,
  INACTIVE_SUBSCRIPTION_ROUTES,
  INIT_APP_ROUTES,
} from './routes';
import createQueryConfig from './queries';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { window } from './env/browser';
import { findRouteLink } from './deprecated-router';
import { changeLocation } from './actions';
import { useAppDispatch } from './shared/hooks/use-app-dispatch';
import { SUBSCRIPTION_STATUS } from './constants';
import EVENT_BUS_EVENTS from './constants/event-bus-events';
import useMerchantSettings from './hooks/use-merchant-settings';
import { useInitializeError } from './hooks/use-initialize-error';

import { OfflinePage } from './app/pages/OfflinePage';
import { EmailVerificationPage } from './app/pages/EmailVerificationPage';

export type CreateRouter = (
  routes: RouteObject[]
) => ReturnType<typeof createBrowserRouter>;

function Loading({ create }: { create: CreateRouter }) {
  return <RouterProvider router={create(INIT_APP_ROUTES)} />;
}

function isSpecialClick(event: MouseEvent): boolean {
  return event.ctrlKey || event.metaKey || event.shiftKey;
}

function subscriptionInactive(
  subscription: { status?: string } | null | undefined
): boolean {
  if (subscription) {
    return (
      subscription.status === SUBSCRIPTION_STATUS.CANCELLED ||
      subscription.status === SUBSCRIPTION_STATUS.PAUSED
    );
  }

  return false;
}

function Router({
  create,
  origin,
  merchantId,
}: {
  create: CreateRouter;
  origin: () => string;
  merchantId: string | null | undefined;
}) {
  const { eventBus, queryClient, apiClient } = useGlobals();
  const dispatch = useAppDispatch();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const {
    data: subscription,
    status: subscriptionQueryStatus,
    isFetchedAfterMount: isSubscriptionFetchingAfterMount,
  } = useQuery(queryConfig.subscription({ merchantId }));
  const isInitialSubscriptionLoading =
    subscriptionQueryStatus === 'pending' && !isSubscriptionFetchingAfterMount;

  const router = useMemo(() => {
    return subscriptionInactive(subscription)
      ? create(INACTIVE_SUBSCRIPTION_ROUTES)
      : create(ROUTES);
  }, [subscription, create]);

  function subscribeToEventBusNavigate() {
    function navigateTo({
      payload: { route },
    }: {
      payload: { route: string };
    }) {
      router.navigate(route);
    }

    eventBus.on(EVENT_BUS_EVENTS.NAVIGATE, navigateTo);

    return () => {
      eventBus.off(EVENT_BUS_EVENTS.NAVIGATE, navigateTo);
    };
  }

  function subscribeToDeprecatedRouterLinkClick() {
    dispatch(changeLocation(origin()));

    function handleRouteLinkClick(event: MouseEvent) {
      if (isSpecialClick(event)) return;

      // event.target is EventTarget, narrower than Node, per the DOM lib;
      // findRouteLink walks up the ancestor chain checking node.tagName, so
      // it needs the wider Node type. The found node is assumed to be an
      // anchor (findRouteLink only matches nodes with an `href` attribute).
      const routeLink = findRouteLink(event.target as Node | null) as
        | HTMLAnchorElement
        | undefined;

      if (!routeLink) return;

      event.preventDefault();

      const { pathname, hash, search } = new URL(routeLink.href);
      router.navigate(pathname + search + hash);
      dispatch(changeLocation(routeLink.href));
    }

    window.addEventListener('click', handleRouteLinkClick);

    return () => {
      window.removeEventListener('click', handleRouteLinkClick);
    };
  }

  useEffect(subscribeToEventBusNavigate, [router]);
  useEffect(subscribeToDeprecatedRouterLinkClick, [router]);

  if (isInitialSubscriptionLoading) {
    return <Loading create={create} />;
  }

  return <RouterProvider router={router} />;
}

function currentLocation(): string {
  return window.location.href;
}

function RouterContainer({
  create,
  origin = currentLocation,
}: {
  create: CreateRouter;
  origin?: () => string;
}) {
  const { isLoading: isLoadingSettings, merchantSettings } =
    useMerchantSettings();

  const { hasInitializationError, initializationErrorName } =
    useInitializeError();

  if (hasInitializationError) {
    if (
      initializationErrorName &&
      initializationErrorName === 'UnconfirmedEmailError'
    ) {
      return <EmailVerificationPage />;
    }
    return <OfflinePage />;
  }

  if (isLoadingSettings) {
    return <Loading create={create} />;
  }

  return (
    <Router
      create={create}
      origin={origin}
      merchantId={merchantSettings.merchantId}
    />
  );
}

export default RouterContainer;
